import { useEffect, useRef, useState } from 'react';
import type { CustomerExistenceResult } from '@/api/customers';

export interface ExistenceState {
  checking: boolean;
  /** The existence result once a check completes for the current value; null while empty/invalid. */
  result: CustomerExistenceResult | null;
}

/**
 * Debounced (500ms) existence check for the email/phone wizard fields. Runs
 * `check(value)` only when `enabled(value)` passes; stale responses are dropped
 * (a monotonically increasing request id guards against out-of-order resolves).
 * A failed request is treated as "no known duplicate" so the API layer / backend
 * stays the source of truth on submit.
 */
export function useExistenceCheck(
  value: string,
  enabled: (v: string) => boolean,
  check: (v: string) => Promise<CustomerExistenceResult>,
): ExistenceState {
  const [state, setState] = useState<ExistenceState>({ checking: false, result: null });
  const reqId = useRef(0);

  useEffect(() => {
    const v = (value || '').trim();
    if (!enabled(v)) {
      reqId.current += 1; // invalidate any in-flight check
      setState({ checking: false, result: null });
      return;
    }

    const id = ++reqId.current;
    setState((s) => ({ ...s, checking: true }));
    const timer = setTimeout(async () => {
      try {
        const result = await check(v);
        if (id === reqId.current) setState({ checking: false, result });
      } catch {
        if (id === reqId.current) setState({ checking: false, result: null });
      }
    }, 500);

    return () => clearTimeout(timer);
    // `enabled`/`check` are stable module functions; only re-run on value change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return state;
}
